import { useMemo } from 'react'
import { Icon } from '@iconify/react'
import { getFlavor, getPairings, type Flavor } from '../data'
import { getPairLevel, pairKey, type FlavorLevel } from '../data/flavorLevels'
import {
  getCategoryColor,
  getCategoryShort,
  getFlavorEmoji,
  getLevelColors,
} from '../data/flavorMeta'
import { getPairDescription } from '../data/pairDescriptions'
import { getFlavorImage } from '../data/flavorImages'

interface PairDetailProps {
  /** Основной вкус (с чьей страницы пришли). */
  flavor: Flavor
  /** Slug вкуса-пары. */
  pairSlug: string
  onBack: () => void
  /** Открыть другую пару основного вкуса (flavor × slug). */
  onOpenPair: (slug: string) => void
}

const LEVEL_TITLE: Record<FlavorLevel, string> = {
  classic: 'Классическое сочетание',
  interesting: 'Необычное сочетание',
  bold: 'Смелое сочетание',
}

/** Половина hero-карточки пары: картинка вкуса + тег категории. */
const PairHalf = ({ flavor }: { flavor: Flavor }) => {
  const image = getFlavorImage(flavor.slug)
  return (
    <div className="pair-hero__half">
      {image ? (
        <img src={image} alt={flavor.name} />
      ) : (
        <span
          className="pair-hero__fallback"
          style={{ background: `${getCategoryColor(flavor.category)}33` }}
          aria-hidden="true"
        >
          {getFlavorEmoji(flavor.slug)}
        </span>
      )}
      <span className="pair-hero__tag">
        <span
          className="pair-hero__tag-dot"
          style={{ background: getCategoryColor(flavor.category) }}
          aria-hidden="true"
        />
        {getCategoryShort(flavor.category)}
      </span>
    </div>
  )
}

/** Карточка «другого сочетания» (product-card-md), открывает пару flavor × slug. */
const OtherPairCard = ({
  slug,
  onOpen,
}: {
  slug: string
  onOpen: (slug: string) => void
}) => {
  const target = getFlavor(slug)
  if (!target) return null
  const image = getFlavorImage(slug)
  return (
    <button type="button" className="pf-md" onClick={() => onOpen(slug)}>
      <span className="pf-md__img">
        {image ? (
          <img src={image} alt="" loading="lazy" />
        ) : (
          <span
            className="pf-md__img-fallback"
            style={{ background: `${getCategoryColor(target.category)}33` }}
            aria-hidden="true"
          >
            {getFlavorEmoji(slug)}
          </span>
        )}
      </span>
      <span className="pf-md__content">
        <span className="pf-md__name">{target.name}</span>
        <span className="pf-md__taste">
          <span
            className="pf-md__dot"
            style={{ background: getCategoryColor(target.category) }}
            aria-hidden="true"
          />
          <span className="pf-md__cat">{getCategoryShort(target.category)}</span>
        </span>
      </span>
    </button>
  )
}

/**
 * Экран пары вкусов: A + B. Hero из двух половин, уровень сочетания и его
 * описание, затем другие сочетания основного вкуса. Рецептов пока нет.
 */
const PairDetail = ({ flavor, pairSlug, onBack, onOpenPair }: PairDetailProps) => {
  const pair = getFlavor(pairSlug)

  const level = pair ? getPairLevel(flavor.slug, pair.slug) : null
  const description = useMemo(
    () =>
      pair
        ? getPairDescription(
            pairKey(flavor.slug, pair.slug),
            flavor.slug,
            pair.slug,
            level,
          )
        : '',
    [flavor.slug, pair, level],
  )

  const others = useMemo(
    () =>
      getPairings(flavor).filter(
        p => p.slug !== pairSlug && getFlavor(p.slug),
      ),
    [flavor, pairSlug],
  )

  // Неизвестный вкус-пара — возвращаемся к вкусу.
  if (!pair) {
    return (
      <div className="flavor-detail">
        <button type="button" className="flavors-back" onClick={onBack}>
          <Icon icon="solar:arrow-left-linear" width={20} height={20} />
          {flavor.name}
        </button>
        <p className="flavors-empty">Сочетание не найдено</p>
      </div>
    )
  }

  const levelColors = getLevelColors(level)

  return (
    <div className="flavor-detail">
      <button type="button" className="flavors-back" onClick={onBack}>
        <Icon icon="solar:arrow-left-linear" width={20} height={20} />
        {flavor.name}
      </button>

      <h1 className="flavor-detail__title">
        {flavor.name} + {pair.name}
      </h1>

      {/* Hero-карточка пары */}
      <div className="pair-hero">
        <div className="pair-hero__images">
          <PairHalf flavor={flavor} />
          <PairHalf flavor={pair} />
        </div>

        <div className="pair-hero__body">
          <div className="pair-hero__level">
            <span
              className="pair-hero__level-dot"
              style={{ background: levelColors.main }}
              aria-hidden="true"
            />
            <span
              className="pair-hero__level-text"
              style={{ color: levelColors.text }}
            >
              {level ? LEVEL_TITLE[level] : 'Сочетание из книги'}
            </span>
          </div>

          <div className="flavor-hero__block">
            <span className="flavor-hero__label">Описание сочетания</span>
            <p className="flavor-hero__desc">{description}</p>
          </div>
        </div>
      </div>

      {/* Другие сочетания основного вкуса */}
      {others.length > 0 && (
        <section className="flavor-section pair-others">
          <h2 className="flavor-section__title">Другое сочетание</h2>
          <div className="flavors-md-grid">
            {others.map(p => (
              <OtherPairCard key={p.slug} slug={p.slug} onOpen={onOpenPair} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

export default PairDetail
